import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, ChevronDown, ChevronRight, Download, Search } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { missingBillService } from '../services/missingBillService';
import { siteService } from '../services/siteService';
import { usageService } from '../services/usageService';
import { NewBadge } from '../components/NewBadge';
import { addMonths, lastCompletedMonth, monthEnd, monthLabel, monthsInRange } from '../lib/billMonths';
import type { ExpectedSite } from '../lib/missingBills';
import { NO_SITE_FILTERS, SITE_FILTERS, type SiteFilterValues } from '../lib/siteFilters';
import {
  BULK_FILTER_KEYS, MAX_BULK_MONTHS, buildBulkCoverage, bulkCoverageSheets, bulkFilterOptions, bulkSitesToCheck,
  byNmiCoverageSheets, clampMonthRange, coverageSiteValue, gapBatches, gapRanges, type IntervalGapRow,
} from '../lib/usageCoverage';
import { usageGaps, yesterday, type MonthGap } from '../lib/usageGaps';
import { exportMultiSheetXLSX } from '../utils/exportUtils';
import type { BvSite } from '../types/billValidationTypes';

// Tariff Usage Report by NMI: the months (and within them the days) that have no
// interval data in bv_nem_interval. Two tabs: By NMI (one site) and Bulk (every
// enrolled BV site in a period, gaps only). Tab, NMI, period and filters live in
// the URL so a report can be linked to.

const DAY_FMT = new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', timeZone: 'UTC' });

type Tab = 'nmi' | 'bulk';
const TABS: { key: Tab; label: string }[] = [{ key: 'nmi', label: 'By NMI' }, { key: 'bulk', label: 'Bulk' }];

type Update = (patch: Record<string, string>) => void;

const DayChips = ({ month }: { month: MonthGap }) => (
  <div className="flex flex-wrap gap-1.5">
    {month.missingDays.map((d) => (
      <span key={d} title={d} className="rounded border border-red-200 bg-white px-2 py-0.5 text-xs text-red-700">
        {DAY_FMT.format(new Date(`${d}T00:00:00Z`))}
      </span>
    ))}
  </div>
);

export const TariffUsageReportPage = () => {
  const [params, setParams] = useSearchParams();
  const lastDay = useMemo(() => yesterday(), []);
  const tab: Tab = params.get('tab') === 'bulk' ? 'bulk' : 'nmi';

  const update: Update = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <div className="p-6 space-y-4">
      <div>
        <Link to="/bill-validation/report-hub" className="text-xs text-gray-500 hover:underline">← Reports</Link>
        <h1 className="text-xl font-semibold text-gray-900">Tariff Usage Report by NMI</h1>
        <p className="text-sm text-gray-500">
          Months and days with no interval data. Data up to {lastDay}; interval data older than the retention window is purged and shows as missing.
        </p>
      </div>

      <div role="tablist" className="flex gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            data-testid={`tariff-usage-tab-${t.key}`}
            onClick={() => update({ tab: t.key === 'bulk' ? 'bulk' : '' })}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              tab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800',
            )}
          >
            {t.label}{t.key === 'bulk' && <NewBadge className="ml-1.5" />}
          </button>
        ))}
      </div>

      {tab === 'nmi'
        ? <ByNmiView params={params} update={update} lastDay={lastDay} />
        : <BulkView params={params} update={update} lastDay={lastDay} />}
    </div>
  );
};

type ViewProps = { params: URLSearchParams; update: Update; lastDay: string };

// ── By NMI ────────────────────────────────────────────────────────────────────

const ByNmiView = ({ params, update, lastDay }: ViewProps) => {
  const siteId = params.get('site') ?? '';
  const to = params.get('to') || lastDay.slice(0, 7);
  const from = params.get('from') || addMonths(to, -11);

  const [query, setQuery] = useState(params.get('nmi') ?? '');
  const [matches, setMatches] = useState<BvSite[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [site, setSite] = useState<BvSite | null>(null);
  const [covered, setCovered] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const search = async () => {
    const nmi = query.trim();
    if (!nmi) return;
    setSearching(true);
    setError(null);
    const res = await siteService.list({ search: nmi, size: 20, sort: 'nmi', sortDir: 'asc' });
    setSearching(false);
    if (!res.success) { setError(res.error || 'Site search failed'); return; }
    const sites = res.data?.sites ?? [];
    // One hit, or an exact NMI match, is picked straight away.
    const exact = sites.filter((s) => s.nmi?.toUpperCase() === nmi.toUpperCase());
    const pick = sites.length === 1 ? sites[0] : exact.length === 1 ? exact[0] : null;
    setMatches(pick ? null : sites);
    if (pick) update({ site: pick.id, nmi: pick.nmi ?? nmi });
  };

  useEffect(() => {
    if (!siteId) { setSite(null); setCovered(null); return; }
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      const lo = from <= to ? from : to;
      const hi = from <= to ? to : from;
      const [siteRes, datesRes] = await Promise.all([
        siteService.getById(siteId),
        usageService.getCoveredDates(siteId, `${lo}-01`, monthEnd(hi)),
      ]);
      if (!mounted) return;
      if (!siteRes.success) setError(siteRes.error || 'Site not found');
      else if (!datesRes.success) setError(datesRes.error || 'Failed to load usage data');
      setSite(siteRes.data ?? null);
      setCovered(datesRes.data ?? null);
      setLoading(false);
    };
    void load();
    return () => { mounted = false; };
  }, [siteId, from, to]);

  // Newest month first, matching the other month-wise views.
  const rows = useMemo(() => {
    if (!covered) return [];
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    return usageGaps(covered, lo, hi, lastDay).reverse();
  }, [covered, from, to, lastDay]);

  const totalMissing = rows.reduce((n, r) => n + r.missingDays.length, 0);
  const totalExpected = rows.reduce((n, r) => n + r.daysExpected, 0);

  const toggle = (month: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(month)) next.delete(month); else next.add(month);
    return next;
  });

  const exportXlsx = () => {
    if (!site) return;
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    exportMultiSheetXLSX(byNmiCoverageSheets(site.nmi ?? '', rows), `tariff-usage-${site.nmi ?? site.id}-${lo}-to-${hi}`);
  };

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4">
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => { e.preventDefault(); void search(); }}
        >
          <label className="text-xs text-gray-600">
            NMI
            <input
              data-testid="tariff-usage-nmi"
              className="mt-1 block w-48 rounded border px-2 py-1.5 font-mono text-sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. 4102000000"
            />
          </label>
          <Button type="submit" size="sm" disabled={searching || !query.trim()}>
            <Search className="h-4 w-4" /> {searching ? 'Searching…' : 'Find'}
          </Button>
        </form>
        <label className="text-xs text-gray-600">
          From
          <input
            type="month"
            data-testid="tariff-usage-from"
            className="mt-1 block rounded border px-2 py-1 text-sm"
            value={from}
            max={lastDay.slice(0, 7)}
            onChange={(e) => update({ from: e.target.value })}
          />
        </label>
        <label className="text-xs text-gray-600">
          To
          <input
            type="month"
            data-testid="tariff-usage-to"
            className="mt-1 block rounded border px-2 py-1 text-sm"
            value={to}
            max={lastDay.slice(0, 7)}
            onChange={(e) => update({ to: e.target.value })}
          />
        </label>
      </div>

      {matches && (
        <div className="rounded-lg border bg-white p-4 text-sm">
          {matches.length === 0 ? (
            <p className="text-gray-500">No site matches “{query.trim()}”.</p>
          ) : (
            <>
              <p className="mb-2 text-gray-600">{matches.length} sites match — pick one:</p>
              <ul className="space-y-1">
                {matches.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className="font-mono text-blue-700 hover:underline"
                      onClick={() => { setMatches(null); setQuery(s.nmi ?? ''); update({ site: s.id, nmi: s.nmi ?? '' }); }}
                    >
                      {s.nmi || '—'}
                    </button>
                    <span className="ml-2 text-gray-500">
                      {[s.client?.name, s.addr_street, s.addr_city].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {error && <ErrorBanner message={error} />}

      {siteId && loading && <p className="text-sm text-gray-500">Loading usage data…</p>}

      {siteId && !loading && site && covered && (
        <div className="rounded-lg border bg-white">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b px-4 py-3">
            <div>
              <Link to={`/bill-validation/sites/${site.id}`} className="font-mono text-sm text-gray-900 hover:underline">
                {site.nmi}
              </Link>
              <span className="ml-2 text-xs text-gray-500">
                {[site.client?.name, site.addr_street, site.addr_city].filter(Boolean).join(' · ')}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span data-testid="tariff-usage-summary" className="text-xs text-gray-600">
                {totalMissing} of {totalExpected} days missing
              </span>
              <Button type="button" size="sm" variant="outline" data-testid="tariff-usage-export" onClick={exportXlsx} disabled={rows.length === 0}>
                <Download className="h-3.5 w-3.5" /> Export XLSX
              </Button>
            </div>
          </div>
          <table data-testid="tariff-usage-table" className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                <th className="px-4 py-2 font-medium">Month</th>
                <th className="px-4 py-2 font-medium">Days with data</th>
                <th className="px-4 py-2 font-medium">Days missing</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const missing = r.missingDays.length;
                const isOpen = open.has(r.month);
                const status = missing === 0 ? 'Complete' : missing === r.daysExpected ? 'No data' : 'Partial';
                return (
                  <Fragment key={r.month}>
                    <tr data-testid="tariff-usage-month" data-month={r.month}>
                      <td className="px-4 py-2.5">
                        {missing > 0 ? (
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            className="inline-flex items-center gap-1 font-medium text-gray-900 hover:text-blue-700"
                            onClick={() => toggle(r.month)}
                          >
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            {monthLabel(r.month)}
                          </button>
                        ) : (
                          <span className="ml-5 font-medium text-gray-900">{monthLabel(r.month)}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">{r.daysExpected - missing} / {r.daysExpected}</td>
                      <td className="px-4 py-2.5 text-gray-700">{missing}</td>
                      <td className="px-4 py-2.5">
                        <span className={cn(
                          'rounded px-2 py-0.5 text-xs font-medium',
                          status === 'Complete' && 'bg-green-50 text-green-700',
                          status === 'Partial' && 'bg-amber-50 text-amber-800',
                          status === 'No data' && 'bg-red-50 text-red-700',
                        )}>
                          {status}
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr data-testid="tariff-usage-days" data-month={r.month}>
                        <td colSpan={4} className="bg-gray-50 px-4 py-3">
                          <div className="pl-5"><DayChips month={r} /></div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const ErrorBanner = ({ message }: { message: string }) => (
  <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
    <AlertCircle className="h-4 w-4" /> {message}
  </div>
);

// ── Bulk ──────────────────────────────────────────────────────────────────────

const FILTER_LABELS = Object.fromEntries(SITE_FILTERS.map((f) => [f.key, f.label])) as Record<string, string>;

const BulkView = ({ params, update, lastDay }: ViewProps) => {
  const maxMonth = lastDay.slice(0, 7);
  const toParam = params.get('bto') || lastCompletedMonth();
  const fromParam = params.get('bfrom') || addMonths(toParam, -2);
  const [from, to] = clampMonthRange(fromParam, toParam);
  const clamped = from !== (fromParam <= toParam ? fromParam : toParam);
  const filters: SiteFilterValues = {
    ...NO_SITE_FILTERS,
    ...Object.fromEntries(BULK_FILTER_KEYS.map((k) => [k, params.get(k) ?? ''])),
  };

  const [sites, setSites] = useState<ExpectedSite[] | null>(null);
  const [gaps, setGaps] = useState<IntervalGapRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const res = await missingBillService.loadExpectedSites();
      if (!mounted) return;
      if (res.success) setSites(res.data ?? []);
      else setError(res.error || 'Failed to load sites');
    };
    void load();
    return () => { mounted = false; };
  }, []);

  const filterKey = BULK_FILTER_KEYS.map((k) => filters[k]).join('|');
  // Only the sites the report counts are checked, in small batches (167): a
  // filter change asks for its own sites again.
  const checkIds = useMemo(
    () => (sites ? bulkSitesToCheck(sites, from, to, lastDay, filters).map((s) => s.id) : null),
    // filters is rebuilt each render; filterKey carries its value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sites, from, to, lastDay, filterKey],
  );

  useEffect(() => {
    if (!checkIds) return;
    let mounted = true;
    const load = async () => {
      const batches = gapBatches(checkIds, monthsInRange(from, to).length);
      setLoading(true);
      setError(null);
      setProgress({ done: 0, total: batches.length });
      const res = await usageService.getIntervalGaps(from, to, batches, (done) => {
        if (mounted) setProgress({ done, total: batches.length });
      });
      if (!mounted) return;
      if (res.success) setGaps(res.data ?? []);
      else { setGaps(null); setError(res.error || 'Failed to load usage data'); }
      setLoading(false);
    };
    void load();
    return () => { mounted = false; };
  }, [checkIds, from, to]);

  const options = useMemo(() => bulkFilterOptions(sites ?? []), [sites]);
  const report = useMemo(
    () => (sites && gaps ? buildBulkCoverage(sites, gaps, from, to, lastDay, filters) : null),
    // filters is rebuilt each render; filterKey carries its value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sites, gaps, from, to, lastDay, filterKey],
  );

  const toggle = (id: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const openNmi = (site: ExpectedSite) =>
    update({ tab: '', site: site.id, nmi: site.nmi ?? '', from, to: to > maxMonth ? maxMonth : to });

  const exportXlsx = () => {
    if (!report) return;
    exportMultiSheetXLSX(bulkCoverageSheets(report, from, to), `tariff-usage-bulk-${from}-to-${to}`);
  };

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4">
        <label className="text-xs text-gray-600">
          From
          <input
            type="month"
            data-testid="tariff-usage-bulk-from"
            className="mt-1 block rounded border px-2 py-1 text-sm"
            value={from}
            max={maxMonth}
            onChange={(e) => update({ bfrom: e.target.value })}
          />
        </label>
        <label className="text-xs text-gray-600">
          To
          <input
            type="month"
            data-testid="tariff-usage-bulk-to"
            className="mt-1 block rounded border px-2 py-1 text-sm"
            value={to}
            max={maxMonth}
            onChange={(e) => update({ bto: e.target.value })}
          />
        </label>
        {BULK_FILTER_KEYS.map((k) => (
          <label key={k} className="text-xs text-gray-600">
            {FILTER_LABELS[k]}
            <select
              data-testid={`tariff-usage-bulk-${k}`}
              value={filters[k]}
              onChange={(e) => update({ [k]: e.target.value })}
              className={cn(
                'mt-1 block max-w-[12rem] rounded border bg-white px-2 py-1 text-sm',
                filters[k] ? 'border-blue-300 text-blue-800' : 'text-gray-700',
              )}
            >
              <option value="">Any</option>
              {options[k].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
        ))}
      </div>

      {clamped && (
        <p className="text-xs text-amber-700">The period is limited to {MAX_BULK_MONTHS} months: showing {monthLabel(from)} – {monthLabel(to)}.</p>
      )}

      {error && <ErrorBanner message={error} />}

      {(loading || !sites) && !error && (
        <p data-testid="tariff-usage-bulk-progress" className="text-sm text-gray-500">
          {loading && progress && progress.total > 1
            ? `Checking ${(checkIds?.length ?? 0).toLocaleString('en-AU')} sites… ${Math.round((progress.done / progress.total) * 100)}%`
            : 'Loading usage data…'}
        </p>
      )}

      {!loading && report && (
        <div className="rounded-lg border bg-white">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b px-4 py-3">
            <span data-testid="tariff-usage-bulk-summary" className="text-sm text-gray-700">
              {report.sitesChecked.toLocaleString('en-AU')} sites checked · {report.fullyCovered.toLocaleString('en-AU')} fully covered ·{' '}
              <span className={cn(report.withGaps > 0 && 'font-medium text-red-700')}>{report.withGaps.toLocaleString('en-AU')} with gaps</span>
              <span className="ml-2 text-xs text-gray-500">({monthLabel(from)} – {monthLabel(to)}; months before a site was enrolled or after it left are not counted)</span>
            </span>
            <Button type="button" size="sm" variant="outline" data-testid="tariff-usage-bulk-export" onClick={exportXlsx} disabled={report.sitesChecked === 0}>
              <Download className="h-3.5 w-3.5" /> Export XLSX
            </Button>
          </div>
          {report.rows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500">
              {report.sitesChecked === 0 ? 'No enrolled sites match these filters.' : 'Every site has interval data for every day in the period.'}
            </p>
          ) : (
            <table data-testid="tariff-usage-bulk-table" className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                  <th className="px-4 py-2 font-medium">NMI</th>
                  <th className="px-4 py-2 font-medium">Entity</th>
                  <th className="px-4 py-2 font-medium">Retailer</th>
                  <th className="px-4 py-2 font-medium">Months short</th>
                  <th className="px-4 py-2 font-medium">Days missing</th>
                  <th className="px-4 py-2 font-medium">Gaps</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {report.rows.map((r) => {
                  const isOpen = open.has(r.site.id);
                  return (
                    <Fragment key={r.site.id}>
                      <tr data-testid="tariff-usage-bulk-row" data-site={r.site.id}>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              aria-expanded={isOpen}
                              aria-label={isOpen ? 'Hide days' : 'Show days'}
                              className="text-gray-500 hover:text-blue-700"
                              onClick={() => toggle(r.site.id)}
                            >
                              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                            <button
                              type="button"
                              data-testid="tariff-usage-bulk-nmi"
                              title="Open in By NMI"
                              className="font-mono text-blue-700 hover:underline"
                              onClick={() => openNmi(r.site)}
                            >
                              {r.site.nmi || '—'}
                            </button>
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">{coverageSiteValue(r.site, 'entity') || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-700">{coverageSiteValue(r.site, 'retailer') || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-700">{r.monthsShort}</td>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{r.daysMissing}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600">
                          {r.months.map((m) => (
                            <div key={m.month}>
                              {m.missingDays.length === m.daysExpected
                                ? <span className="text-red-700">No data {monthLabel(m.month)}</span>
                                : gapRanges(m.missingDays)}
                            </div>
                          ))}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid="tariff-usage-bulk-days" data-site={r.site.id}>
                          <td colSpan={6} className="bg-gray-50 px-4 py-3">
                            <div className="space-y-2 pl-5">
                              {r.months.map((m) => (
                                <div key={m.month} className="flex flex-wrap items-start gap-3">
                                  <span className="w-20 shrink-0 pt-0.5 text-xs font-medium text-gray-700">{monthLabel(m.month)}</span>
                                  <DayChips month={m} />
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
};
